import { useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { ArrowLeft } from "lucide-react";
import Footer from "@/components/site/Footer";
import { PRIVACY_POLICY, TERMS_OF_USE } from "@/lib/legal";
import { track } from "@/lib/analytics";

function LegalSection({ heading, body = [], list, after = [] }) {
  return (
    <section className="mt-10">
      <h2 className="font-display text-[22px] leading-snug text-natsu-navy sm:text-[26px]">
        {heading}
      </h2>
      {body.map((p, i) => (
        <p
          key={`b-${i}`}
          className="mt-4 max-w-[70ch] text-[15px] leading-[1.75] text-natsu-navy/80 sm:text-[16px]"
        >
          {p}
        </p>
      ))}
      {list && (
        <ul className="mt-4 max-w-[70ch] space-y-2 pl-1">
          {list.map((item, i) => (
            <li
              key={`l-${i}`}
              className="flex items-start gap-3 text-[15px] leading-[1.7] text-natsu-navy/80 sm:text-[16px]"
            >
              <span className="mt-2 h-[5px] w-[5px] flex-shrink-0 rounded-full bg-natsu-gold" />
              <span>{item}</span>
            </li>
          ))}
        </ul>
      )}
      {after.map((p, i) => (
        <p
          key={`a-${i}`}
          className="mt-4 max-w-[70ch] text-[15px] leading-[1.75] text-natsu-navy/80 sm:text-[16px]"
        >
          {p}
        </p>
      ))}
    </section>
  );
}

export default function LegalPage({ kind = "privacy" }) {
  const doc = kind === "terms" ? TERMS_OF_USE : PRIVACY_POLICY;
  const { pathname } = useLocation();

  useEffect(() => {
    // page-view analytics event (no PII)
    track(kind === "terms" ? "terms_of_use_opened" : "privacy_policy_opened");
    if (typeof window !== "undefined") window.scrollTo(0, 0);
  }, [kind, pathname]);

  return (
    <main className="min-h-screen bg-white" data-testid={`legal-${kind}`}>
      {/* Slim header — mirrors the site tone without adding a full navbar to a legal doc */}
      <div className="on-dark bg-natsu-navy">
        <div className="site-container flex h-[72px] items-center">
          <Link
            to="/"
            className="inline-flex items-center gap-2 text-sm font-medium text-natsu-warm-white/80 transition-colors hover:text-natsu-warm-white"
            data-testid="legal-back-home"
          >
            <ArrowLeft className="h-4 w-4" /> Back to NATSU Intelligence
          </Link>
        </div>
      </div>

      <article className="site-container py-16 sm:py-20 lg:py-24">
        <p className="eyebrow text-natsu-slate">Legal</p>
        <h1 className="display mt-5 text-4xl leading-tight text-natsu-navy sm:text-5xl">
          {doc.title}
        </h1>
        <p className="mt-4 text-[13.5px] font-medium tracking-wide text-natsu-slate">
          Effective date: {doc.effectiveDate}
        </p>

        <div className="mt-2">
          {doc.sections.map((s) => (
            <LegalSection key={s.heading} {...s} />
          ))}
        </div>

        <div className="mt-16 border-t border-natsu-navy/10 pt-8">
          <Link
            to="/"
            className="inline-flex items-center gap-2 text-sm font-medium text-natsu-blue transition-colors hover:text-natsu-blue-soft"
          >
            <ArrowLeft className="h-4 w-4" /> Return to NATSU Intelligence
          </Link>
        </div>
      </article>

      <Footer />
    </main>
  );
}
